import { type FormEvent, type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { Head, Link } from '@inertiajs/react'
import { ArrowLeft, Camera, CameraOff, Check, CircleX } from 'lucide-react'
import type { Html5Qrcode as Html5QrcodeInstance } from 'html5-qrcode'
import DashboardLayout from '@/components/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

type TicketRow = {
  id: string
  code: string
  tier_name: string | null
  guest_name: string | null
  checked_in_at: string | null
}

type Counts = { checked_in: number; total: number }

type ScanResult =
  | { result: 'ok'; message: string; ticket: TicketRow; counts: Counts }
  | { result: 'already'; message: string; ticket: TicketRow; counts: Counts }
  | { result: 'refunded'; message: string }
  | { result: 'invalid'; message: string }

type Props = {
  event: { id: string; slug: string; title: string }
  counts: Counts
  tickets: TicketRow[]
}

const SCAN_REGION = 'qr-reader'
const OVERLAY_DURATION_MS = 1500

function csrfToken() {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ''
}

function formatTime(iso: string | null) {
  if (!iso) return null
  return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}

// Full opacity-suffixed Tailwind classes are kept as literals so the JIT
// scanner picks them up. Concatenating the `/95` at render time would NOT
// generate the class.
const VARIANT_STYLES: Record<ScanResult['result'], { bg: string; icon: ReactNode; title: string }> = {
  ok: { bg: 'bg-emerald-500/95', icon: <Check className="h-20 w-20" />, title: 'Checked in' },
  already: { bg: 'bg-amber-500/95', icon: <Check className="h-20 w-20" />, title: 'Already checked in' },
  refunded: { bg: 'bg-red-500/95', icon: <CircleX className="h-20 w-20" />, title: 'Refunded' },
  invalid: { bg: 'bg-red-500/95', icon: <CircleX className="h-20 w-20" />, title: 'Invalid ticket' },
}

export default function CheckInPage({ event, counts: initialCounts, tickets: initialTickets }: Props) {
  const [counts, setCounts] = useState<Counts>(initialCounts)
  const [tickets, setTickets] = useState<TicketRow[]>(initialTickets)
  const [scanning, setScanning] = useState(false)
  const [overlay, setOverlay] = useState<ScanResult | null>(null)
  const [manualCode, setManualCode] = useState('')
  const [submitting, setSubmitting] = useState<string | null>(null)
  const [filter, setFilter] = useState('')
  const [cameraError, setCameraError] = useState<string | null>(null)
  const qrRef = useRef<Html5QrcodeInstance | null>(null)
  const lockRef = useRef(false)
  const overlayTimer = useRef<number | null>(null)

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase()
    if (!q) return tickets
    return tickets.filter(t =>
      [t.code, t.guest_name ?? '', t.tier_name ?? ''].some(v => v.toLowerCase().includes(q))
    )
  }, [filter, tickets])

  useEffect(() => () => {
    if (overlayTimer.current) window.clearTimeout(overlayTimer.current)
    void stopScannerSafely()
  }, [])

  async function stopScannerSafely() {
    const instance = qrRef.current
    if (!instance) return
    try {
      if (instance.isScanning) await instance.stop()
      await instance.clear()
    } catch {
      /* ignore — teardown is best-effort */
    }
    qrRef.current = null
  }

  async function startScanner() {
    setCameraError(null)
    if (qrRef.current) await stopScannerSafely()
    try {
      const { Html5Qrcode } = await import('html5-qrcode')
      const instance = new Html5Qrcode(SCAN_REGION, { verbose: false })
      qrRef.current = instance
      await instance.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 260, height: 260 } },
        decoded => {
          void onScan(decoded)
        },
        () => {}
      )
      setScanning(true)
    } catch (err) {
      qrRef.current = null
      setCameraError(
        err instanceof Error
          ? err.message
          : 'Could not start the camera. Use manual entry or grant permission.'
      )
    }
  }

  async function stopScanner() {
    await stopScannerSafely()
    setScanning(false)
  }

  function showOverlay(result: ScanResult) {
    setOverlay(result)
    // Pause the camera so a stationary QR doesn't fire repeated scans. We
    // resume it (if still scanning) when the overlay times out.
    pauseScanner()
    if (overlayTimer.current) window.clearTimeout(overlayTimer.current)
    overlayTimer.current = window.setTimeout(() => {
      setOverlay(null)
      lockRef.current = false
      resumeScanner()
    }, OVERLAY_DURATION_MS)
  }

  function pauseScanner() {
    const instance = qrRef.current
    if (!instance) return
    try {
      if (instance.isScanning) instance.pause(true)
    } catch {
      /* ignore — pause is best-effort */
    }
  }

  function resumeScanner() {
    const instance = qrRef.current
    if (!instance) return
    try {
      instance.resume()
    } catch {
      /* ignore — resume is best-effort */
    }
  }

  function applyResult(result: ScanResult) {
    if (result.result === 'ok' || result.result === 'already') {
      setCounts(result.counts)
      setTickets(prev =>
        prev.map(t => (t.id === result.ticket.id ? { ...t, checked_in_at: result.ticket.checked_in_at } : t))
      )
    }
  }

  async function submitCode(code: string) {
    if (lockRef.current) return
    lockRef.current = true
    try {
      const res = await fetch(`/dashboard/events/${event.id}/check_ins/scan`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken(),
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ code: code.trim().toUpperCase() }),
      })
      const json = (await res.json()) as ScanResult
      applyResult(json)
      showOverlay(json)
    } catch {
      const fallback: ScanResult = { result: 'invalid', message: 'Network error. Try again.' }
      showOverlay(fallback)
    }
  }

  async function onScan(decoded: string) {
    if (lockRef.current) return
    await submitCode(decoded)
  }

  function onManualSubmit(e: FormEvent) {
    e.preventDefault()
    if (!manualCode.trim()) return
    void submitCode(manualCode)
    setManualCode('')
  }

  async function checkInRow(code: string) {
    if (submitting) return
    setSubmitting(code)
    try {
      await submitCode(code)
    } finally {
      setSubmitting(null)
    }
  }

  return (
    <>
      <Head title={`Check in · ${event.title}`} />
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:py-10">
        <div className="mb-6">
          <Link
            href={`/dashboard/events/${event.id}/edit`}
            className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" /> Back to event
          </Link>
          <div className="flex items-end justify-between gap-4">
            <div>
              <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">Check in</h1>
              <p className="mt-1 text-muted-foreground">{event.title}</p>
            </div>
            <div className="text-right">
              <div className="text-sm text-muted-foreground">Checked in</div>
              <div className="text-3xl font-semibold tracking-tight text-primary">
                {counts.checked_in} <span className="text-muted-foreground">/ {counts.total}</span>
              </div>
            </div>
          </div>
        </div>

        <Card className="mb-4">
          <CardHeader>
            <CardTitle>Scanner</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="relative mx-auto aspect-square w-full max-w-md overflow-hidden rounded-xl bg-muted">
              {/* The qr-reader div MUST be empty and stable — html5-qrcode
                  injects video + canvas children. Any React-managed children
                  inside it would race with React diffing and tear out the
                  lib's nodes (AbortError on play()). */}
              <div id={SCAN_REGION} className="absolute inset-0" />
              {!scanning && (
                <div className="absolute inset-0 flex items-center justify-center px-6 text-center text-sm text-muted-foreground">
                  Tap &ldquo;Start scanner&rdquo; to scan QR codes from confirmation emails.
                </div>
              )}
            </div>
            {cameraError && (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                {cameraError}
              </div>
            )}
            <div className="flex justify-center">
              {scanning ? (
                <Button type="button" variant="secondary" onClick={() => void stopScanner()} className="gap-2">
                  <CameraOff className="h-4 w-4" /> Stop scanner
                </Button>
              ) : (
                <Button type="button" onClick={() => void startScanner()} className="gap-2">
                  <Camera className="h-4 w-4" /> Start scanner
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="mb-4">
          <CardHeader>
            <CardTitle>Manual entry</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={onManualSubmit} className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <div className="flex-1 space-y-2">
                <Label htmlFor="manual-code">Ticket code</Label>
                <Input
                  id="manual-code"
                  value={manualCode}
                  onChange={e => setManualCode(e.target.value)}
                  placeholder="ABC123XYZ456"
                  inputMode="text"
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  pattern="[A-Za-z0-9]{12}"
                  maxLength={12}
                />
              </div>
              <Button type="submit" disabled={!manualCode.trim()}>
                Check in
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Attendees</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <Input
                value={filter}
                onChange={e => setFilter(e.target.value)}
                placeholder="Filter by name, code, or tier"
              />
            </div>
            {filtered.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                {tickets.length === 0
                  ? 'No tickets sold yet.'
                  : 'No attendees match that filter.'}
              </p>
            ) : (
              <ul className="divide-y divide-black/5">
                {filtered.map(ticket => {
                  const checkedAt = formatTime(ticket.checked_in_at)
                  return (
                    <li key={ticket.id} className="flex items-center justify-between gap-3 py-3">
                      <div className="min-w-0">
                        <div className="truncate font-medium">{ticket.guest_name ?? '—'}</div>
                        <div className="text-xs text-muted-foreground">
                          <span className="font-mono">{ticket.code}</span>
                          {ticket.tier_name && <span> · {ticket.tier_name}</span>}
                        </div>
                      </div>
                      {ticket.checked_in_at ? (
                        <span className="inline-flex items-center gap-1 text-sm font-medium text-emerald-600">
                          <Check className="h-4 w-4" /> {checkedAt}
                        </span>
                      ) : (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => void checkInRow(ticket.code)}
                          disabled={submitting === ticket.code}
                        >
                          {submitting === ticket.code ? 'Checking…' : 'Check in'}
                        </Button>
                      )}
                    </li>
                  )
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {overlay && (
        <div
          className={`fixed inset-0 z-50 flex flex-col items-center justify-center px-6 text-center text-white ${
            VARIANT_STYLES[overlay.result].bg
          }`}
        >
          <div className="mb-4">{VARIANT_STYLES[overlay.result].icon}</div>
          <div className="text-3xl font-semibold tracking-tight">{VARIANT_STYLES[overlay.result].title}</div>
          <div className="mt-2 max-w-sm text-base opacity-90">{overlay.message}</div>
          {(overlay.result === 'ok' || overlay.result === 'already') && (
            <div className="mt-4 text-sm opacity-90">
              {overlay.ticket.tier_name && <div>{overlay.ticket.tier_name}</div>}
              <div className="font-mono">{overlay.ticket.code}</div>
            </div>
          )}
        </div>
      )}
    </>
  )
}

CheckInPage.layout = (page: ReactNode) => <DashboardLayout>{page}</DashboardLayout>
